import React, { useEffect, useState, useRef } from "react";
import Loader from "../../../shared/loader/loader";
import {
  AddIcon,
  EditIcon,
  DeleteIcon,
  CaretUpSolidIcon,
  CaretDownSolidIcon,
} from "../../../../assets/icons/index";
import SelectPageSize from "../../../shared/common/pageSizeDropdown";
import Pagination from "../../../shared/common/pagination";
import { IPermission } from "../../../../model/interface/permission";
import Constant from "../../../../utils/constant";
import DeleteModal from "../../../shared/common/deleteModal";
import { useNavigate } from "react-router";
import {
  deletePermission,
  getPermissions,
} from "../../../../api/permission.service";
import AllowedTo from "../../../shared/common/rbac";
import pagePermissions from "../../../../utils/permissions";
import AccessDenied from "../../../shared/common/accessDenied";

function Permissions() {
  const defaultPageNumber = 1;
  const defaultPageSize = 10;
  const navigate = useNavigate();
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [pageNumber, setPageNumber] = useState(defaultPageNumber);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [permissions, setPermissions] = useState<IPermission[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [showDeletePopup, setShowDeletePopup] = useState<boolean>(false);
  const [sortAscending, setSortAscending] = useState<boolean>(true);
  const [orderByColumn, setOrderByColumn] = useState<string>("");
  const [sortDir, setSortDir] = useState<string>("");
  const [idToDelete, setIDToDelete] = useState<number>();
  const paginationRef: any = useRef(undefined);
  const pageSizeRef: any = useRef(undefined);
  const [totalRecords, setTotalRecords] = useState<number>(0);

  function bindPermissions(isReset?) {
    setLoading(true);
    getPermissions(pageNumber, pageSize, sortDir, orderByColumn).then(
      (response) => {
        setLoading(false);
        setPermissions(response.data.data);
        setTotalPages(
          Math.ceil(
            response.data.totalRecords / (isReset ? defaultPageSize : pageSize)
          )
        );
        setTotalRecords(response.data.totalRecords);
      }
    );
  }

  function confirmDelete(idToDelete?: number) {
    if (idToDelete) {
      setShowDeletePopup(true);
      setIDToDelete(idToDelete);
    }
  }

  function deleteRecord() {
    if (idToDelete) {
      setLoading(true);
      deletePermission(idToDelete).then(() => {
        setLoading(false);
        setShowDeletePopup(false);
        setIDToDelete(0);
        bindPermissions();
      });
    }
  }

  function closeDeletePopup() {
    setShowDeletePopup(false);
    setIDToDelete(0);
  }

  const handleRedirect = (permissionId?: number) => {
    navigate(
      permissionId
        ? Constant.EDIT_PERMISSION.replace(
            ":id",
            permissionId ? permissionId.toString() : ""
          )
        : Constant.ADD_PERMISSION
    );
  };

  function handleSort(columnName) {
    setOrderByColumn(columnName);
    setSortAscending(!sortAscending);
    setSortDir(sortAscending ? "DESC" : "ASC");

    bindPermissions();
  }

  useEffect(() => {
    if (isLoaded) {
      bindPermissions();
    }
  }, [pageNumber]);

  useEffect(() => {
    if (pageNumber === 1) {
      bindPermissions();

      setIsLoaded(true);
    } else {
      paginationRef?.current?.onReset();
    }
  }, [pageSize]);

  return (
    <AllowedTo
      perform={pagePermissions.permissions_view}
      no={() => <AccessDenied />}
    >
      {loading && <Loader />}
      {showDeletePopup && (
        <DeleteModal
          closeDeleteModal={closeDeletePopup}
          deleteRecord={deleteRecord}
        />
      )}
      <div className="flex-1">
        <div className=" h-auto min-h-half w-full rounded-bl-3xl bg-site-teal px-8  py-8 pt-0 pb-14 mt-7 ">
          <div className="relative  py-1">
            <div className="overlay-image-main pointer-events-none fixed left-0 top-0 w-screen h-screen z-20"></div>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-light text-gray-900">Permissions</h3>
              <AllowedTo perform={pagePermissions.permissions_create}>
                <button
                  onClick={() => handleRedirect()}
                  type="button"
                  className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 rounded-md shadow-xs text-white dark:text-white bg-primary-color hover:bg-secondary-color focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color hover:shadow-lg transsition ease-in-out duration-300"
                >
                  <AddIcon className="w-5"></AddIcon>
                  Add Permission
                </button>
              </AllowedTo>
            </div>

            <div id="dataContainer" className="mt-4">
              <div className="flex flex-col mt-1">
                <div className="-my-2 sm:-mx-6 lg:-mx-8">
                  <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div className="shadow border-b border-gray-200 sm:rounded-lg">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-primary-color">
                          <tr>
                            <th
                              scope="col"
                              className="px-3 py-2 pl-6 font-light text-sm text-left text-white dark:text-white tracking-wider rounded-tl-lg"
                            >
                              <span className="flex">
                                Name
                                <span className="cursor-pointer">
                                  {!sortAscending &&
                                  orderByColumn === "name" ? (
                                    <CaretUpSolidIcon
                                      className="w-5 h-5"
                                      onClick={() => handleSort("name")}
                                    ></CaretUpSolidIcon>
                                  ) : (
                                    <CaretDownSolidIcon
                                      className="w-5 h-5"
                                      onClick={() => handleSort("name")}
                                    ></CaretDownSolidIcon>
                                  )}
                                </span>
                              </span>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 pl-6 font-light text-sm text-left text-white dark:text-white tracking-wider"
                            >
                              <span className="flex">
                                Description
                                <span className="cursor-pointer">
                                  {!sortAscending &&
                                  orderByColumn === "description" ? (
                                    <CaretUpSolidIcon
                                      className="w-5 h-5"
                                      onClick={() => handleSort("description")}
                                    ></CaretUpSolidIcon>
                                  ) : (
                                    <CaretDownSolidIcon
                                      className="w-5 h-5"
                                      onClick={() => handleSort("description")}
                                    ></CaretDownSolidIcon>
                                  )}
                                </span>
                              </span>
                            </th>

                            <th
                              scope="col"
                              className="w-12 px-3 py-2 font-light text-sm text-center text-white dark:text-white tracking-wider rounded-tr-lg"
                            >
                              Action
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          {permissions.map((permission, index) => (
                            <tr
                              key={permission.permissionId}
                              className={
                                index % 2 === 0 ? "bg-white" : "bg-gray-100"
                              }
                            >
                              <td className="px-3 py-2  pl-6 whitespace-wrap text-sm text-left font-normal text-gray-500">
                                {permission.name}
                              </td>
                              <td className="px-3 py-2  pl-6 whitespace-wrap text-sm text-left font-normal text-gray-500">
                                {permission.description}
                              </td>

                              <td className="px-3 py-2 whitespace-nowrap text-sm text-center font-normal text-gray-700">
                                <div className="flex gap-3 justify-center">
                                  <AllowedTo
                                    perform={pagePermissions.permissions_modify}
                                  >
                                    <EditIcon
                                      onClick={(e) =>
                                        handleRedirect(permission.permissionId)
                                      }
                                      className="w-4 text-xs font-light cursor-pointer hover:text-secondary-color transaction ease-in-out duration-300"
                                    />
                                  </AllowedTo>
                                  <AllowedTo
                                    perform={pagePermissions.permissions_delete}
                                  >
                                    <DeleteIcon
                                      onClick={() =>
                                        confirmDelete(permission.permissionId)
                                      }
                                      className="w-4 text-xs font-light cursor-pointer hover:text-red-500 transaction ease-in-out duration-300"
                                    />
                                  </AllowedTo>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                <div
                  className={`flex justify-between items-center ${
                    permissions.length === 0 ? "hidden" : ""
                  }`}
                >
                  <Pagination
                    pages={totalPages}
                    setPageNumber={setPageNumber}
                    ref={paginationRef}
                  />
                  <span>
                    <span className="text-sm pr-4 ml-auto text-left-700">
                      Showing {pageNumber} to{" "}
                      {totalRecords < pageSize ? totalRecords : pageSize} of{" "}
                      {totalRecords} results
                    </span>

                    <SelectPageSize
                      setPageSize={setPageSize}
                      type={"second"}
                      ref={pageSizeRef}
                    />
                  </span>
                </div>
                {permissions.length === 0 && (
                  <p className="p-2">No Records Found</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AllowedTo>
  );
}

export default Permissions;
